import { Box, Button, Code, Flex, VStack } from "@chakra-ui/react";
import AppPage from "layouts/AppPage";
import { CDS } from "design-system";
import { FloatingField } from "design-system/components/FloatingField";
import { FloatingFieldSize } from "design-system/components/FloatingField/context";
import { LocationDetails } from "components/Form/Select/LocationSelect";
import { NewHireJourney } from "models/v2/newHireJourney";
import { apiNewHireListPath } from "helpers/url/api/v2";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import useNavbar from "hooks/useNavbar";

interface FormValues {
  firstName: string;
  lastName: string;
  workEmail: string;
  personalEmail: string;
  startDate: string;
  startTime: string;
  timezone: string;
  newHireJourney: string;
  newHires: string[];
  department: string;
  employmentType: string;
  roles: string[];
  bio: string;
  notes: string;
}

interface FloatingFieldExamplePageProps {
  size: FloatingFieldSize;
}

export default function FloatingFieldExamplePage({ size }: FloatingFieldExamplePageProps) {
  const { setPageTitle } = useNavbar();
  const [submitted, setSubmitted] = useState<FormValues | null>(null);
  const [location, setLocation] = useState<LocationDetails | null>(null);

  useEffect(() => {
    setPageTitle(`FloatingField — ${size.toUpperCase()}`);
  }, [setPageTitle, size]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: "Kevin",
      lastName: "Berry",
      workEmail: "kevin@example.com",
      personalEmail: "",
      startDate: "",
      startTime: "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      newHireJourney: "",
      newHires: [],
      department: "Engineering",
      employmentType: "full_time",
      roles: ["onboarder"],
      bio: "",
      notes: "",
    },
  });

  const onSubmit = (values: FormValues) => {
    setSubmitted(values);
  };

  return (
    <AppPage>
      <Box maxW="540px" mx="auto" py="8" px="4">
        <CDS.Heading20 mb="1">FloatingField — {size.toUpperCase()}</CDS.Heading20>
        <CDS.Text14 color="gray.500" mb="8">
          All sub-components rendered at <Code fontSize="sm">size=&quot;{size}&quot;</Code>
        </CDS.Text14>

        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={4} align="stretch">
            <FloatingField name="firstName" size={size} errors={errors.firstName?.message} required>
              <FloatingField.Label>First Name</FloatingField.Label>
              <FloatingField.Text control={control} name="firstName" rules={{ required: "First name is required" }} />
            </FloatingField>

            <FloatingField name="lastName" size={size} errors={errors.lastName?.message} required>
              <FloatingField.Label>Last Name</FloatingField.Label>
              <FloatingField.Text control={control} name="lastName" rules={{ required: "Last name is required" }} />
            </FloatingField>

            <FloatingField name="workEmail" size={size} errors={errors.workEmail?.message} required>
              <FloatingField.Label>Work Email</FloatingField.Label>
              <FloatingField.Text
                control={control}
                name="workEmail"
                type="email"
                rules={{
                  required: "Work email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email",
                  },
                }}
              />
            </FloatingField>

            <FloatingField name="personalEmail" size={size} errors={errors.personalEmail?.message}>
              <FloatingField.Label>Personal Email</FloatingField.Label>
              <FloatingField.Text control={control} name="personalEmail" type="email" />
            </FloatingField>

            <FloatingField name="startDate" size={size} errors={errors.startDate?.message} required>
              <FloatingField.Label>Start Date</FloatingField.Label>
              <FloatingField.Calendar
                control={control}
                name="startDate"
                rules={{ required: "Start date is required" }}
                isClearable
              />
            </FloatingField>

            <FloatingField name="startTime" size={size} errors={errors.startTime?.message}>
              <FloatingField.Label>Start Time</FloatingField.Label>
              <FloatingField.TimePicker control={control} name="startTime" />
            </FloatingField>

            <FloatingField name="bio" size={size} errors={errors.bio?.message}>
              <FloatingField.Label>Bio</FloatingField.Label>
              <FloatingField.TextArea control={control} name="bio" rows={3} />
            </FloatingField>

            <FloatingField name="notes" size={size} errors={errors.notes?.message}>
              <FloatingField.Label>Notes</FloatingField.Label>
              <FloatingField.Trix control={control} name="notes" />
            </FloatingField>

            <FloatingField name="timezone" size={size} errors={errors.timezone?.message} required>
              <FloatingField.Label>Timezone</FloatingField.Label>
              <FloatingField.Timezone control={control} name="timezone" rules={{ required: "Timezone is required" }} />
            </FloatingField>

            <FloatingField name="location" size={size}>
              <FloatingField.Label>Location</FloatingField.Label>
              <FloatingField.Location onChange={(value) => setLocation(value)} />
            </FloatingField>

            <FloatingField name="newHireJourney" size={size} errors={errors.newHireJourney?.message} required>
              <FloatingField.Label>New Hire</FloatingField.Label>
              <FloatingField.SingleSelect<NewHireJourney>
                control={control}
                name="newHireJourney"
                fetchPath={apiNewHireListPath()}
                extractValue={(v: NewHireJourney) => v.id}
                embedOption={(v: NewHireJourney) => ({
                  value: v.id,
                  label: v.newHire.fullName,
                  rawValue: v,
                })}
                rules={{ required: "New hire is required" }}
                placeholder=""
              />
            </FloatingField>

            <FloatingField name="newHires" size={size} errors={errors.newHires?.message}>
              <FloatingField.Label>New Hires (multi)</FloatingField.Label>
              <FloatingField.AsyncMultiSelect<NewHireJourney>
                control={control}
                name="newHires"
                fetchPath={apiNewHireListPath()}
                extractValue={(v: NewHireJourney) => v.id}
                embedOption={(v: NewHireJourney) => ({
                  value: v.id,
                  label: v.newHire.fullName,
                  rawValue: v,
                })}
                placeholder=""
              />
            </FloatingField>

            <FloatingField name="department" size={size} errors={errors.department?.message}>
              <FloatingField.Label>Department</FloatingField.Label>
              <FloatingField.CreatableSingle
                control={control}
                name="department"
                resourceUrl={API_ROUTES.departments.base}
                placeholder=""
              />
            </FloatingField>

            <FloatingField name="employmentType" size={size} errors={errors.employmentType?.message} required>
              <FloatingField.Label>Employment Type</FloatingField.Label>
              <FloatingField.StaticSelect
                control={control}
                name="employmentType"
                rules={{ required: "Employment type is required" }}
                options={[
                  { value: "full_time", label: "Full Time" },
                  { value: "part_time", label: "Part Time" },
                  { value: "contractor", label: "Contractor" },
                  { value: "intern", label: "Intern" },
                ]}
              />
            </FloatingField>

            <FloatingField name="roles" size={size} errors={(errors as any).roles?.message}>
              <FloatingField.Label>Roles</FloatingField.Label>
              <FloatingField.MultiSelect
                control={control}
                name="roles"
                options={[
                  { value: "onboarder", label: "Onboarder" },
                  { value: "manager", label: "Manager" },
                  { value: "people_team", label: "People Team" },
                  { value: "basic", label: "Basic Employee" },
                  { value: "company_admin", label: "Company Admin" },
                ]}
                menuPlacement="top"
              />
            </FloatingField>

            <Flex justify="flex-end" pt="2">
              <Button type="submit" colorScheme="brand" size={size === "sm" ? "sm" : "md"}>
                Submit
              </Button>
            </Flex>
          </VStack>
        </form>

        {(submitted || location) && (
          <Box mt="8" p="4" bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
            <CDS.Heading14 mb="3">Submitted Values</CDS.Heading14>
            <VStack align="stretch" spacing={1}>
              {submitted &&
                Object.entries(submitted).map(([key, value]) => {
                  const display =
                    !value || (Array.isArray(value) && value.length === 0)
                      ? "(empty)"
                      : value instanceof Date
                        ? value.toLocaleDateString()
                        : Array.isArray(value)
                          ? value.join(", ")
                          : String(value);
                  return (
                    <Flex key={key} gap={2}>
                      <Code fontSize="xs" minW="120px">
                        {key}
                      </Code>
                      <CDS.Text12 color={value ? "gray.800" : "gray.400"}>{display}</CDS.Text12>
                    </Flex>
                  );
                })}
              {location && (
                <Flex gap={2}>
                  <Code fontSize="xs" minW="120px">
                    location
                  </Code>
                  <CDS.Text12 color="gray.800">{location.displayName}</CDS.Text12>
                </Flex>
              )}
            </VStack>
          </Box>
        )}
      </Box>
    </AppPage>
  );
}
